import { useQuery } from '@apollo/client';
import { Input, Link, Pill, Select, Stack, Table, TableColumns, Text } from '@sixfold/common-ui';
import React, { useMemo } from 'react';
import { useSearchParams } from 'react-router';

import { EmptyState } from './ui';
import { MobileCompaniesQuery, MobileCompaniesQueryVariables, notEmpty } from '../../lib/graphql';
import { Routes } from '../../routes';
import { mobileCompaniesQuery } from '../graphql';
import {
  COMPANIES_PER_FLAG,
  CompanyStats,
  EMPTY_STATS,
  MOBILE_FEATURE_FLAGS,
  MOBILE_FLAG_LABEL,
  MobileCompany,
  MobileFeatureFlag,
} from '../hooks';

import styles from './mobile.module.css';

interface Row {
  id: string;
  companyName: string;
  mobile: number;
  tracked: number;
  needsAction: number;
  failed: number;
  enabledFlags: MobileFeatureFlag[];
}

const percent = (part: number, total: number) => (total === 0 ? '-' : `${Math.round((part / total) * 100)}%`);

const columns: TableColumns<Row> = [
  {
    id: 'companyName',
    title: 'Company',
    minWidth: '220px',
    cellContent: ({ row }) => (
      <div className={styles.cellStack}>
        <Link href={Routes.MobileCompany.generatePath({ company_id: row.id })} weight="bold">
          {row.companyName}
        </Link>
        <Text size="small" prominence="subtle">
          {row.id}
        </Text>
      </div>
    ),
  },
  { id: 'mobile', title: 'Mobile transports', align: 'end' },
  {
    id: 'tracked',
    title: 'Positions received',
    align: 'end',
    cellContent: ({ row }) => (row.mobile === 0 ? '-' : `${row.tracked} (${percent(row.tracked, row.mobile)})`),
  },
  {
    id: 'needsAction',
    title: 'Needs action',
    align: 'end',
    cellContent: ({ row }) => (row.needsAction > 0 ? <Pill kind="danger">{row.needsAction}</Pill> : '0'),
  },
  { id: 'failed', title: 'Failed', align: 'end' },
  {
    id: 'enabledFlags',
    title: 'Mobile setup',
    cellContent: ({ row }) => (
      <Stack gap="xsmall">
        {row.enabledFlags.map((flag) => (
          <Pill key={flag}>{MOBILE_FLAG_LABEL[flag]}</Pill>
        ))}
      </Stack>
    ),
  },
];

const useFlaggedCompanies = (enabled: boolean) => {
  const allocation = useQuery<MobileCompaniesQuery, MobileCompaniesQueryVariables>(mobileCompaniesQuery, {
    variables: { featureFlag: MOBILE_FEATURE_FLAGS[0], first: COMPANIES_PER_FLAG },
    skip: !enabled,
  });
  const trackingWithoutAllocation = useQuery<MobileCompaniesQuery, MobileCompaniesQueryVariables>(
    mobileCompaniesQuery,
    {
      variables: { featureFlag: MOBILE_FEATURE_FLAGS[1], first: COMPANIES_PER_FLAG },
      skip: !enabled,
    },
  );
  const automaticTtc = useQuery<MobileCompaniesQuery, MobileCompaniesQueryVariables>(mobileCompaniesQuery, {
    variables: { featureFlag: MOBILE_FEATURE_FLAGS[2], first: COMPANIES_PER_FLAG },
    skip: !enabled,
  });
  const legacyAuth = useQuery<MobileCompaniesQuery, MobileCompaniesQueryVariables>(mobileCompaniesQuery, {
    variables: { featureFlag: MOBILE_FEATURE_FLAGS[3], first: COMPANIES_PER_FLAG },
    skip: !enabled,
  });
  const results = [allocation, trackingWithoutAllocation, automaticTtc, legacyAuth];
  const companies = useMemo(() => {
    const byId = new Map<string, MobileCompany>();
    results.forEach((result, index) => {
      (result.data?.companies?.edges ?? [])
        .map((edge) => edge?.node)
        .filter(notEmpty)
        .forEach((node) => {
          const existing = byId.get(node.company_id) ?? {
            companyId: node.company_id,
            companyName: node.company_name ?? node.company_id,
            enabledFlags: [],
          };
          const flag = MOBILE_FEATURE_FLAGS[index];
          if (flag !== undefined && !existing.enabledFlags.includes(flag)) {
            existing.enabledFlags.push(flag);
          }
          byId.set(node.company_id, existing);
        });
    });
    return [...byId.values()];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allocation.data, trackingWithoutAllocation.data, automaticTtc.data, legacyAuth.data]);

  return {
    companies,
    loading: enabled && results.some((result) => result.loading),
    error: results.find((result) => result.error)?.error,
  };
};

export const CompaniesTable: React.FC<{ companies: MobileCompany[]; statsByCompany: Map<string, CompanyStats> }> = ({
  companies,
  statsByCompany,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') ?? '';
  const flagParam = searchParams.get('flag');
  const flag = MOBILE_FEATURE_FLAGS.find((candidate) => candidate === flagParam) ?? 'all';
  const activeOnly = searchParams.get('active') !== 'false';
  const flagged = useFlaggedCompanies(!activeOnly);
  const listedCompanies = useMemo(() => {
    if (activeOnly) {
      return companies;
    }
    const byId = new Map(companies.map((company) => [company.companyId, company]));
    flagged.companies.forEach((company) => {
      if (!byId.has(company.companyId)) {
        byId.set(company.companyId, company);
      }
    });
    return [...byId.values()];
  }, [activeOnly, companies, flagged.companies]);

  const setParam = (key: string, value: string, defaultValue: string) => {
    const next = new URLSearchParams(searchParams);
    if (value === defaultValue) {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    setSearchParams(next, { replace: true });
  };

  const rows: Row[] = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return listedCompanies
      .map((company) => {
        const stats = statsByCompany.get(company.companyId) ?? EMPTY_STATS;
        return {
          id: company.companyId,
          companyName: company.companyName,
          mobile: stats.mobile,
          tracked: stats.tracked,
          needsAction: stats.needsAction,
          failed: stats.failed,
          enabledFlags: company.enabledFlags,
        };
      })
      .filter(
        (row) =>
          (!activeOnly || row.mobile > 0) &&
          (flag === 'all' || row.enabledFlags.includes(flag)) &&
          (needle === '' || row.companyName.toLowerCase().includes(needle) || row.id.toLowerCase().includes(needle)),
      )
      .sort((a, b) => b.mobile - a.mobile || b.needsAction - a.needsAction);
  }, [listedCompanies, statsByCompany, query, flag, activeOnly]);

  return (
    <Stack direction="column" wrap={false} gap="medium">
      <div className={styles.filterBar}>
        <div className={styles.filterSearch}>
          <Input
            label="Search"
            labelPosition="hidden"
            size="small"
            name="mobile-company-search"
            icon="small-search"
            placeholder="Company name or ID"
            value={query}
            clearable
            onClear={() => setParam('q', '', '')}
            onChange={({ value }) => setParam('q', value, '')}
          />
        </div>
        <div className={styles.filterSelect}>
          <Select
            label="Feature flag"
            name="mobile-company-flag"
            value={flag}
            options={[
              { value: 'all', label: 'Any mobile flag' },
              ...MOBILE_FEATURE_FLAGS.map((value) => ({ value, label: MOBILE_FLAG_LABEL[value] })),
            ]}
            onChange={(option) => setParam('flag', option?.value ?? 'all', 'all')}
          />
        </div>
        <div className={styles.filterSelect}>
          <Select
            label="Activity"
            name="mobile-company-activity"
            value={activeOnly ? 'active' : 'all'}
            options={[
              { value: 'active', label: 'With mobile transports' },
              { value: 'all', label: 'All flagged companies' },
            ]}
            onChange={(option) => setParam('active', option?.value === 'all' ? 'false' : 'true', 'true')}
          />
        </div>
        <Text prominence="subtle">
          {rows.length} of {listedCompanies.length} companies, sorted by mobile transports
          {flagged.loading ? '. Loading companies without transports.' : ''}
        </Text>
      </div>
      {!activeOnly && flagged.error !== undefined && <Text prominence="subtle">{flagged.error.message}</Text>}
      {rows.length === 0 && !flagged.loading ? (
        <EmptyState
          title="No companies match"
          description="Clear the search or include companies without transports."
        />
      ) : (
        <Table data={rows} columns={columns} />
      )}
    </Stack>
  );
};
